import { useReactToPrint } from "react-to-print";

/**
 * Lê o CSS JÁ CARREGADO na página (CSSOM em memória), sem tocar na rede.
 *
 * Percorre document.styleSheets e concatena o cssText de cada regra. As folhas
 * cross-origin (ex.: fontes de CDN) lançam SecurityError ao aceder a cssRules —
 * são ignoradas (essas têm URL estável e carregam normalmente na impressão).
 */
function coletarCssCarregado() {
  let css = "";
  for (const sheet of Array.from(document.styleSheets)) {
    let rules;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin → sem acesso; ignorar
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      css += rule.cssText + "\n";
    }
  }
  return css;
}

/**
 * Hook de impressão robusto a deploys.
 *
 * Problema que resolve: o `react-to-print` (copyStyles: true, por defeito) clona
 * os <link> de CSS para o iframe de impressão, o que faz o iframe VOLTAR A
 * BUSCAR o `main.[hash].css`. Após um deploy, a aba antiga aponta para um hash
 * que já não existe no servidor (404) → a impressão sai sem estilos
 * (desconfigurada), até se fazer refresh.
 *
 * Aqui desligamos essa clonagem (copyStyles: false) e injetamos, via pageStyle,
 * o CSS que a aba JÁ tem em memória (o mesmo que a está a estilizar agora).
 * Assim a impressão nunca depende de re-fetch → imune a deploys, mesmo numa aba
 * que não foi atualizada.
 *
 * @param {Object}   opts
 * @param {Object}   opts.contentRef          ref para o conteúdo a imprimir
 * @param {String}   opts.documentTitle       título do documento (nome do PDF)
 * @param {Function} [opts.onAfterPrint]
 * @param {Function} [opts.onBeforeGetContent]
 * @param {String}   [opts.pageStyle="@page { size: A4 portrait; }"]  regras @page
 */
export default function usePrintDocument({
  contentRef,
  documentTitle,
  onAfterPrint,
  onBeforeGetContent,
  pageStyle = "@page { size: A4 portrait; }",
}) {
  return useReactToPrint({
    content: () => contentRef?.current,
    documentTitle,
    onAfterPrint,
    onBeforeGetContent,
    // Não clonar os <link> (é o re-fetch do CSS com hash que falha após deploy).
    copyStyles: false,
    // Em vez disso, injetar o CSS já carregado (da memória) + as regras @page.
    pageStyle: () => `${pageStyle}\n${coletarCssCarregado()}`,
  });
}
